'use client';

import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import FilterSidebar, { type FilterState, type FilterOption } from './FilterSidebar';
import {
  WorkshopGrid,
  type Workshop,
  AppIcon,
  WORKSHOP_CATEGORIES,
} from '@impacta/shared';

const DEFAULT_FILTERS: FilterState = {
  categories: [],
  priceRange: [0, 200000],
  levels: [],
  locations: [],
  durationRanges: [],
  dateFrom: '',
  dateTo: '',
};

interface ActivitiesClientProps {
  workshops: Workshop[];
}

export default function ActivitiesClient({ workshops }: ActivitiesClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const searchQuery = (searchParams?.get('q') ?? '').trim().toLowerCase();
  const urlCategory = (searchParams?.get('category') ?? '').trim();
  const urlMaxPrice = (searchParams?.get('maxPrice') ?? '').trim();
  const urlDateFrom = (searchParams?.get('dateFrom') ?? '').trim();
  const urlDateTo = (searchParams?.get('dateTo') ?? '').trim();

  const maxPrice = useMemo(() => {
    if (workshops.length === 0) return 200000;
    return Math.ceil(Math.max(...workshops.map((w) => w.priceNum)) / 5000) * 5000;
  }, [workshops]);

  const [filters, setFilters] = useState<FilterState>(() => {
    const mp = urlMaxPrice ? parseInt(urlMaxPrice, 10) : NaN;
    return {
      categories: urlCategory && urlCategory !== 'Todas' ? [urlCategory] : [],
      priceRange: [0, Number.isFinite(mp) && mp > 0 ? mp : DEFAULT_FILTERS.priceRange[1]],
      levels: [],
      locations: DEFAULT_FILTERS.locations,
      durationRanges: [],
      dateFrom: urlDateFrom,
      dateTo: urlDateTo,
    };
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Re-sync when URL params change (e.g. user clicks another chip in HeaderSearch
  // while already on /actividades — the page does not remount).
  useEffect(() => {
    const mp = urlMaxPrice ? parseInt(urlMaxPrice, 10) : NaN;
    const nextCategories = urlCategory && urlCategory !== 'Todas' ? [urlCategory] : [];
    const nextMax = Number.isFinite(mp) && mp > 0 ? Math.min(mp, maxPrice) : maxPrice;
    setFilters((prev) => {
      const sameCats =
        prev.categories.length === nextCategories.length &&
        prev.categories.every((c, i) => c === nextCategories[i]);
      const sameDates = prev.dateFrom === urlDateFrom && prev.dateTo === urlDateTo;
      if (sameCats && prev.priceRange[1] === nextMax && sameDates) return prev;
      return {
        ...prev,
        categories: nextCategories,
        priceRange: [0, nextMax],
        dateFrom: urlDateFrom,
        dateTo: urlDateTo,
      };
    });
  }, [urlCategory, urlMaxPrice, maxPrice, urlDateFrom, urlDateTo]);

  const dateBounds = useMemo(() => {
    const dates = workshops
      .map((w) => w.earliestSessionDate)
      .filter((d): d is string => Boolean(d))
      .sort();
    return {
      minDate: dates[0] || undefined,
      maxDate: dates[dates.length - 1] || undefined,
    };
  }, [workshops]);

  const categoryOptions: FilterOption[] = useMemo(() => {
    const actualCategories = [
      ...new Set(workshops.map((w) => w.category).filter(Boolean)),
    ];
    const combined = [...new Set([...WORKSHOP_CATEGORIES, ...actualCategories])];
    return combined.map((c) => ({
      value: c,
      label: c,
    }));
  }, [workshops]);

  const levelOptions: FilterOption[] = useMemo(() => {
    const levels = [...new Set(workshops.map((w) => w.level).filter(Boolean))].sort();
    return levels.map((l) => ({ value: l as string, label: l as string }));
  }, [workshops]);

  const locationOptions: FilterOption[] = useMemo(() => {
    const comunas = [...new Set(workshops.map((w) => w.locationComuna).filter(Boolean))].sort();
    return comunas.map((c) => ({
      value: c as string,
      label: c as string,
    }));
  }, [workshops]);

  const durationBuckets: FilterOption[] = useMemo(() => {
    const hasAnyDuration = workshops.some((w) => w.durationMinutes != null);
    if (!hasAnyDuration) return [];
    return [
      { value: '0-120', label: 'Menos de 2 hrs' },
      { value: '120-180', label: '2–3 hrs' },
      { value: '180-300', label: '3–5 hrs' },
      { value: '300+', label: 'Más de 5 hrs' },
    ];
  }, [workshops]);

  const activeCount = useMemo(() => {
    return (
      filters.categories.length +
      filters.levels.length +
      filters.locations.length +
      filters.durationRanges.length +
      (filters.priceRange[1] < maxPrice ? 1 : 0) +
      (filters.dateFrom ? 1 : 0) +
      (filters.dateTo ? 1 : 0)
    );
  }, [filters, maxPrice]);

  const filtered = useMemo(() => {
    return workshops.filter((w) => {
      if (searchQuery) {
        const haystack = [w.title, w.category, w.teacher].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(searchQuery)) return false;
      }
      if (filters.categories.length && !filters.categories.includes(w.category)) return false;
      if (w.priceNum > filters.priceRange[1]) return false;
      if (filters.levels.length && w.level && !filters.levels.includes(w.level)) return false;
      if (
        filters.locations.length &&
        w.locationComuna &&
        !filters.locations.includes(w.locationComuna)
      )
        return false;
      if (filters.durationRanges.length && w.durationMinutes != null) {
        const inRange = filters.durationRanges.some((range) => {
          if (range === '300+') return w.durationMinutes! >= 300;
          const [min, max] = range.split('-').map(Number);
          return w.durationMinutes! >= min && w.durationMinutes! < max;
        });
        if (!inRange) return false;
      }
      if (filters.dateFrom || filters.dateTo) {
        const sessionDate = w.earliestSessionDate;
        if (!sessionDate) return false;
        if (filters.dateFrom && sessionDate < filters.dateFrom) return false;
        if (filters.dateTo && sessionDate > filters.dateTo) return false;
      }
      return true;
    });
  }, [workshops, filters, searchQuery]);

  const defaultFilters: FilterState = {
    categories: [],
    priceRange: [0, maxPrice],
    levels: [],
    locations: [],
    durationRanges: [],
    dateFrom: '',
    dateTo: '',
  };

  const handleClear = () => {
    setFilters(defaultFilters);
    router.replace('/actividades', { scroll: false });
  };

  return (
    <>
      {/* Mobile filter toggle */}
      <div className="lg:hidden mb-4">
        <button
          onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E8E8E4] text-sm font-semibold text-[#373A36] bg-white"
        >
          <AppIcon name="FunnelIcon" size={16} />
          Filtros
          {activeCount > 0 && (
            <span
              className="w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center text-white"
              style={{ backgroundColor: '#00A19B' }}
            >
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {/* Mobile filter drawer */}
      {mobileFiltersOpen && (
        <div className="lg:hidden mb-6 bg-white rounded-2xl p-5 border border-[#E8E8E4]">
          <FilterSidebar
            filters={filters}
            onChange={setFilters}
            onClear={handleClear}
            activeCount={activeCount}
            categoryOptions={categoryOptions}
            maxPrice={maxPrice}
            levelOptions={levelOptions}
            locationOptions={locationOptions}
            durationBuckets={durationBuckets}
            minDate={dateBounds.minDate}
            maxDate={dateBounds.maxDate}
          />
        </div>
      )}

      <div className="flex gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="bg-white rounded-2xl p-5 sticky top-24">
            <FilterSidebar
              filters={filters}
              onChange={setFilters}
              onClear={handleClear}
              activeCount={activeCount}
              categoryOptions={categoryOptions}
              maxPrice={maxPrice}
              levelOptions={levelOptions}
              locationOptions={locationOptions}
              durationBuckets={durationBuckets}
              minDate={dateBounds.minDate}
              maxDate={dateBounds.maxDate}
            />
          </div>
        </aside>

        {/* Grid */}
        <div className="flex-1 min-w-0">
          <WorkshopGrid workshops={filtered} total={workshops.length} />
        </div>
      </div>
    </>
  );
}
